/**
 * The canonical monetary type for Progear Finance 2.0 (replit.md L9).
 *
 * Every monetary amount in the system is a `Money`, never a bare `number`.
 * Amounts are integer cents — JS floats are never used for money.
 *
 * SCOPE: this module defines the TYPE and integer-cents conversion helpers ONLY.
 * VAT / margin / gross computation lives in the NET/VAT/GROSS engine
 * (Phase 1, module 1.2) inside finance-core — deliberately not here.
 */

/** How a VAT rate is applied to an amount. */
export type VatMode = 'domestic' | 'foreign' | 'reduced' | 'exempt';

/** A monetary amount with explicit NET / VAT / GROSS parts, all in integer cents. */
export interface Money {
  /** Net amount in integer cents. */
  net: number;
  /** VAT amount in integer cents. */
  vat: number;
  /** Gross amount in integer cents (net + vat). */
  gross: number;
  /** VAT rate as a whole-number percentage, e.g. 24, 13, 9, 0. */
  vatRate: number;
  /** How the VAT rate is applied. */
  vatMode: VatMode;
}

/** True when `value` is a whole number of cents (a safe integer). */
export function isWholeCents(value: number): boolean {
  return Number.isSafeInteger(value);
}

/**
 * Convert a euro amount (decimal) to integer cents, rounded to the nearest cent
 * (half away from zero, so positive and negative amounts are symmetric).
 *
 * Rounding happens on the *decimal* value, not the raw binary product, so
 * values like `1.005` become `101` rather than `100` — binary floating-point
 * drift (`1.005 * 100 === 100.49999999999999`) is absorbed before rounding.
 *
 * Throws on non-finite input, and on any input whose cent value would fall
 * outside the safe-integer range, so bad data surfaces instead of silently
 * becoming `NaN` or an imprecise integer.
 */
export function toCents(euros: number): number {
  if (!Number.isFinite(euros)) {
    throw new RangeError(`toCents: expected a finite number, received ${euros}`);
  }
  const scaled = Number((euros * 100).toPrecision(15));
  const cents = Math.sign(scaled) * Math.round(Math.abs(scaled));
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(`toCents: amount ${euros} is outside the safe integer-cents range`);
  }
  return cents;
}

/**
 * Convert an integer-cent amount in a FOREIGN currency to EUR integer cents using a
 * SmartAccounts-style exchange rate — the rate SA prints on a foreign-currency
 * document, expressed as FOREIGN units per 1 EUR (e.g. USD `1.1742` = "1 EUR buys
 * 1.1742 USD"; a EUR document carries `1`). EUR is therefore `foreign / rate`.
 *
 * Rounding is half away from zero on the decimal quotient — symmetric for a credit
 * note's negative amount, and the same rule as {@link toCents}; binary drift is
 * absorbed via `toPrecision(15)` before the rounding.
 *
 * Throws on a non-positive or non-finite rate (a zero rate would divide-by-zero and
 * a missing/garbled rate must never silently pass), on non-integer input cents, and
 * on a result outside the safe-integer range — bad data surfaces instead of becoming
 * a mis-booked amount (L9).
 */
export function foreignCentsToEur(foreignCents: number, exchangeRate: number): number {
  if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
    throw new RangeError(
      `foreignCentsToEur: exchangeRate must be a positive finite number, received ${exchangeRate}`,
    );
  }
  if (!isWholeCents(foreignCents)) {
    throw new RangeError(`foreignCentsToEur: expected whole cents, received ${foreignCents}`);
  }
  const scaled = Number((foreignCents / exchangeRate).toPrecision(15));
  const cents = Math.sign(scaled) * Math.round(Math.abs(scaled));
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(
      `foreignCentsToEur: ${foreignCents} cents at rate ${exchangeRate} is outside the safe integer-cents range`,
    );
  }
  return cents;
}

/**
 * Parse a decimal money STRING (`"1234.56"`, optionally negative) directly to
 * integer cents WITHOUT ever going through a float (L9). This is the boundary
 * parser for documents that print exact two-decimal amounts (e.g. the
 * SmartAccounts palgaleht PDF): exactly two decimals, dot separator, no
 * thousands separators — anything else throws so a misread amount surfaces
 * instead of silently importing wrong money.
 */
export function centsFromDecimalString(value: string): number {
  const m = /^(-?)(\d+)\.(\d{2})$/.exec(value.trim());
  if (!m) {
    throw new RangeError(`centsFromDecimalString: not a two-decimal money string: "${value}"`);
  }
  const cents = Number(m[2]) * 100 + Number(m[3]);
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(`centsFromDecimalString: amount "${value}" is outside the safe integer-cents range`);
  }
  return m[1] === '-' ? -cents : cents;
}

/**
 * Convert integer cents to a euro amount (decimal) for display only.
 * Throws if `cents` is not a whole number of cents.
 */
export function fromCents(cents: number): number {
  if (!isWholeCents(cents)) {
    throw new RangeError(`fromCents: expected whole cents, received ${cents}`);
  }
  return cents / 100;
}
